import { useTranslation } from 'react-i18next';

import styles from '../StepPanel.module.scss';
import { AlignerEditForm } from './AlignerEditForm';
import {
  type AlignerSet,
  MINUTES_PER_DAY,
  MINUTES_PER_HOUR,
  resolveDisplayStatus,
} from './buildPatch';
import {
  clampPercent,
  progressFillClass,
  statusBadgeClass,
  statusLabelKey,
} from './displayHelpers';

type AlignerProgressRowProps = {
  readonly aligner: AlignerSet;
  readonly patientId: string;
  readonly treatmentId: string;
  readonly cycleId: string;
  readonly isEditing: boolean;
  readonly onToggleEdit: () => void;
  readonly onCloseEdit: () => void;
};

export function AlignerProgressRow({
  aligner,
  patientId,
  treatmentId,
  cycleId,
  isEditing,
  onToggleEdit,
  onCloseEdit,
}: AlignerProgressRowProps) {
  const { t } = useTranslation();
  const displayStatus = resolveDisplayStatus(aligner);
  const statusLabel = t(statusLabelKey(displayStatus));
  const progressPercent = clampPercent(aligner.progress_percent ?? 0);
  const effectiveMinutes = aligner.effective_worn_minutes;
  const targetDays = aligner.target_wear_days;
  const wornDaysFloor = Math.floor(effectiveMinutes / MINUTES_PER_DAY);

  const isCurrentlyWearing = displayStatus === 'wearing';
  const remainderMinutes = effectiveMinutes - wornDaysFloor * MINUTES_PER_DAY;
  const hoursPart = Math.floor(remainderMinutes / MINUTES_PER_HOUR);
  const showHours = isCurrentlyWearing && remainderMinutes > 0;

  const wornText = showHours
    ? t('wearing.progress.daysHoursWorn', { days: wornDaysFloor, hours: hoursPart })
    : t('wearing.progress.daysWornOfTarget', {
        worn: wornDaysFloor,
        target: targetDays ?? '—',
      });

  const canEdit = aligner.status !== 'not_started';

  return (
    <div className={styles.alignerRow}>
      <div className={styles.alignerRowHeader}>
        <div className={styles.alignerRowTitleGroup}>
          <span className={styles.alignerRowLabel}>
            {t('wearing.progress.alignerLabel', { number: aligner.aligner_number })}
          </span>
          <span className={`${styles.statusBadgeProgress} ${statusBadgeClass(displayStatus)}`}>
            {statusLabel}
          </span>
        </div>
        {canEdit && (
          <button
            type="button"
            className={styles.editToggleBtn}
            onClick={onToggleEdit}
            aria-expanded={isEditing}
            aria-label={t('wearing.progress.editAlignerN', { number: aligner.aligner_number })}
          >
            {isEditing ? t('wearing.progress.cancel') : t('wearing.progress.edit')}
          </button>
        )}
      </div>

      <div className={styles.progressBar}>
        <div
          className={`${styles.progressFill} ${progressFillClass(displayStatus)}`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <div className={styles.progressFooterText}>{wornText}</div>

      {isEditing && (
        <AlignerEditForm
          aligner={aligner}
          patientId={patientId}
          treatmentId={treatmentId}
          cycleId={cycleId}
          onClose={onCloseEdit}
        />
      )}
    </div>
  );
}
